/** The feed itself: one line per thing that happened, newest first.
 *
 * A timeline rather than a table, because the question it answers is "what
 * happened" and the eye reads that down a column of dates. Each line is the
 * time, a dot in the kind's tone, who did what, the circuit when the feed
 * is not already one circuit's, the detail, and the links out — the survey,
 * the issue, the pull request. Days are headed so a night's run reads as
 * one block.
 *
 * The same component serves three screens: the whole feed under Logs, a
 * circuit's own history, and the handful of lines on the Dashboard. The
 * filters are chips, one per kind, and any number may be on; none on is
 * everything.
 */

import { useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';

import { adminActivity } from '@/lib/api';
import { clockTime, shortDate } from '@/lib/format';
import type { ActivityEvent, ActivityKind, ActivityPage } from '@/lib/types';
import { EmptyState, ErrorNote, LoadingPanel } from '@/components/ui';
import { LoadMore } from '@/components/admin/LoadMore';
import { usePaged } from '@/components/admin/usePaged';
import { ACTIVITY_FILTERS, activityLine, activityLinks, activityTrack } from '@/components/admin/activity';
import type { ActivityTone } from '@/components/admin/activity';

export interface ActivityFeedProps {
  /** One circuit's feed; absent is everybody's. */
  officialId?: string;
  /** The kinds switched on; empty is every kind. */
  kinds?: readonly ActivityKind[];
  onKindsChange?: (kinds: ActivityKind[]) => void;
  /** Lines per page. */
  limit?: number;
  /** No chips, no load-more: the Dashboard's few lines. */
  compact?: boolean;
}

const MUTED = 'var(--color-neutral-500)';
const QUIET = 'var(--color-neutral-600)';

const TONE: Record<ActivityTone, string> = {
  ok: 'var(--color-ok)',
  warn: 'var(--color-warn)',
  danger: 'var(--color-error)',
  accent: 'var(--color-accent)',
  neutral: 'var(--color-neutral-500)',
};

/** `2026-10-13` → the day heading. */
function dayOf(at: string): string {
  return at.slice(0, 10);
}

export function ActivityFeed({
  officialId,
  kinds = [],
  onKindsChange,
  limit = 50,
  compact = false,
}: ActivityFeedProps) {
  const kindParam = kinds.join(',');
  const fetcher = useCallback(
    (cursor: string | undefined) =>
      adminActivity({
        ...(officialId ? { official_id: officialId } : {}),
        ...(kindParam ? { kind: kindParam } : {}),
        limit,
        ...(cursor ? { cursor } : {}),
      }),
    [officialId, kindParam, limit],
  );
  const listing = usePaged<ActivityEvent, ActivityPage>(fetcher);

  const days = useMemo(() => {
    const groups: Array<{ day: string; events: ActivityEvent[] }> = [];
    for (const item of listing.items) {
      const day = dayOf(item.at);
      const last = groups[groups.length - 1];
      if (last && last.day === day) last.events.push(item);
      else groups.push({ day, events: [item] });
    }
    return groups;
  }, [listing.items]);

  const toggle = (kind: ActivityKind) => {
    if (!onKindsChange) return;
    onKindsChange(kinds.includes(kind) ? kinds.filter((k) => k !== kind) : [...kinds, kind]);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: compact ? 8 : 14, minWidth: 0 }}>
      {!compact && onKindsChange ? (
        <div role="group" aria-label="kinds" style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {ACTIVITY_FILTERS.map((filter) => {
            const on = kinds.includes(filter.kind);
            return (
              <button
                key={filter.kind}
                type="button"
                className={on ? 'btn btn-primary' : 'btn btn-secondary'}
                style={{ fontSize: 12 }}
                aria-pressed={on}
                onClick={() => toggle(filter.kind)}
              >
                {filter.label}
              </button>
            );
          })}
          {kinds.length > 0 ? (
            <button type="button" className="btn btn-ghost" style={{ fontSize: 12 }} onClick={() => onKindsChange([])}>
              Everything
            </button>
          ) : null}
        </div>
      ) : null}

      {listing.error ? (
        <ErrorNote error={listing.error} title="could not read what happened" onRetry={listing.reload} />
      ) : listing.loading ? (
        <LoadingPanel label="reading what happened" />
      ) : listing.items.length === 0 ? (
        <EmptyState title="Nothing has happened here yet" compact>
          {officialId
            ? 'No survey has arrived for this circuit, and nothing has been decided about it.'
            : 'Once a survey arrives or the merge job runs, it is written here.'}
        </EmptyState>
      ) : (
        <ol style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: compact ? 2 : 4 }}>
          {days.map((group) => (
            <li key={group.day}>
              {!compact ? (
                <div
                  className="tabular-nums"
                  style={{ fontSize: 11, color: QUIET, textTransform: 'uppercase', letterSpacing: '0.04em', padding: '10px 0 4px' }}
                >
                  {shortDate(group.day)}
                </div>
              ) : null}
              <ol style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                {group.events.map((item) => (
                  <ActivityRow key={item.id} event={item} showTrack={!officialId} compact={compact} />
                ))}
              </ol>
            </li>
          ))}
        </ol>
      )}

      {!compact ? <LoadMore listing={listing} /> : null}
    </div>
  );
}

function ActivityRow({ event, showTrack, compact }: { event: ActivityEvent; showTrack: boolean; compact: boolean }) {
  const line = activityLine(event);
  const links = activityLinks(event);
  const track = activityTrack(event);
  return (
    <li
      style={{
        display: 'grid',
        gridTemplateColumns: 'auto auto minmax(0, 1fr)',
        gap: '0 10px',
        alignItems: 'baseline',
        padding: compact ? '4px 0' : '6px 0',
        borderTop: '1px solid var(--color-divider)',
        fontSize: compact ? 12.5 : 13,
      }}
    >
      <span className="tabular-nums" style={{ color: MUTED, fontSize: 11.5, whiteSpace: 'nowrap' }}>
        {compact ? `${shortDate(event.at)} ${clockTime(event.at)}` : clockTime(event.at)}
      </span>
      <span
        aria-hidden="true"
        style={{ width: 7, height: 7, borderRadius: 7, background: TONE[line.tone], display: 'inline-block', transform: 'translateY(-1px)' }}
      />
      <span style={{ minWidth: 0, overflowWrap: 'anywhere' }}>
        <span style={{ fontWeight: 500 }}>{line.who}</span>{' '}
        <span>{line.what}</span>
        {showTrack && track ? (
          <>
            {' '}
            <span style={{ color: MUTED }}>·</span>{' '}
            <Link to={`/admin/tracks/${encodeURIComponent(event.official_id)}`} style={{ color: 'var(--color-neutral-300)' }}>
              {track}
            </Link>
          </>
        ) : null}
        {line.detail ? (
          <span style={{ color: MUTED }}>
            {' '}
            · {line.detail}
          </span>
        ) : null}
        {links.length > 0 && !compact ? (
          <span style={{ marginLeft: 8, fontSize: 11.5 }}>
            {links.map((link, index) => (
              <span key={link.label + link.to}>
                {index > 0 ? ' · ' : ''}
                {link.external ? (
                  <a href={link.to} target="_blank" rel="noreferrer">
                    {link.label}
                  </a>
                ) : (
                  <Link to={link.to}>{link.label}</Link>
                )}
              </span>
            ))}
          </span>
        ) : null}
      </span>
    </li>
  );
}

export default ActivityFeed;
